import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Download, GraduationCap } from 'lucide-react';
import type { OnboardingTaskKind, TrainingTaskSummary } from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import { useCurrentUser } from '../../hooks/useAuth';
import { Hero } from '../../components/ui/Hero';
import { EmptyState } from '../../components/ui/EmptyState';
import { Card, CardSection } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { SkeletonList } from '../../components/ui/Skeleton';
import { ModulePlayer } from '../../components/training/player/ModulePlayer';

interface MeTask {
  task_id: string;
  title: string;
  description: string | null;
  status: 'pending' | 'awaiting_review' | 'changes_requested' | 'complete';
  onboarding_kind?: OnboardingTaskKind;
  training?: TrainingTaskSummary | null;
}
interface MeView {
  tasks: MeTask[];
}

interface TrainingPlayerProps {
  /** Where the back link points. Defaults to the onboarding self-service
   *  page; the training portal passes its own home instead. */
  backTo?: string;
  backLabel?: string;
}

/**
 * Dedicated page for taking one training module. Reached from /me (main app)
 * or the training portal home; the player owns paging, autosave and
 * submission. A module that was ALREADY finished when the page opened renders
 * a completion summary instead of the player (the task is locked).
 *
 * "Already finished when the page opened" is the important part: submitting
 * refetches /api/me, which flips the task to complete. Branching on the live
 * status would tear the player's result screen out from under the learner the
 * instant it appeared. The result screen owns the end of the attempt; the
 * summary is for coming back later.
 */
export function TrainingPlayer({
  backTo = '/me',
  backLabel = 'Back to your onboarding',
}: TrainingPlayerProps = {}) {
  const { taskId } = useParams<{ taskId: string }>();
  const { data: user, isLoading: sessionLoading } = useCurrentUser();
  // Latched on the first load that has data; see the note above.
  const openedComplete = useRef<boolean | null>(null);

  const me = useQuery({
    queryKey: ['me-view'],
    enabled: !!user?.person_id,
    queryFn: () => api<MeView>('/api/me'),
  });

  if (sessionLoading) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <SkeletonList rows={4} />
      </main>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.person_id) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        This page is for attendees.{' '}
        <Link to="/" className="underline">
          Home
        </Link>
        .
      </main>
    );
  }
  if (me.isLoading) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <SkeletonList rows={4} />
      </main>
    );
  }
  if (me.error) {
    const status = me.error instanceof ApiError ? me.error.status : 0;
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <EmptyState
          icon={<AlertCircle className="h-6 w-6" />}
          title="Couldn't load this training"
          description={`The server returned ${status}.`}
        />
      </main>
    );
  }

  const task = me.data?.tasks.find((t) => t.task_id === taskId);
  if (task && openedComplete.current === null) {
    openedComplete.current = task.status === 'complete';
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-2xl space-y-5 px-4 py-8 sm:px-6">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-sm font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </Link>

        {!task || task.onboarding_kind !== 'training' ? (
          <EmptyState
            icon={<AlertCircle className="h-6 w-6" />}
            title="Training not found"
            description="This training module isn't assigned to you."
          />
        ) : openedComplete.current ? (
          <>
            <Hero
              eyebrow={<span>Training</span>}
              title={task.title}
              description={
                task.training?.status === 'passed' && task.training.best_score != null
                  ? 'Completed. Your certificate is below.'
                  : 'Completed.'
              }
            />
            <Card>
              <CardSection className="flex flex-col items-center gap-3 py-8 text-center">
                <GraduationCap className="h-8 w-8 text-success-soft-fg" />
                <Pill tone="success">
                  {/* An unmarked assessment is completed, never passed. */}
                  {task.training?.status === 'passed' && task.training.graded !== false
                    ? `Passed${task.training.best_score != null ? ` · ${task.training.best_score}%` : ''}`
                    : 'Complete'}
                </Pill>
                {/* The certificate route 404s without a passing graded attempt
                    (external/honesty modules and admin completions have none),
                    so only link when a scored pass exists. best_score is null
                    for those, so it's the reliable "has a certificate" signal. */}
                {task.training?.status === 'passed' && task.training.best_score != null ? (
                  <a
                    href={`/api/me/training/${task.task_id}/certificate.pdf`}
                    className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
                  >
                    <Download className="h-4 w-4" />
                    Download your certificate
                  </a>
                ) : null}
              </CardSection>
            </Card>
          </>
        ) : (
          <>
            <Hero
              eyebrow={<span>Training</span>}
              title={task.title}
              description={task.description ?? 'Complete this training module.'}
            />
            <Card>
              <CardSection>
                <ModulePlayer taskId={task.task_id} backTo={backTo} backLabel={backLabel} />
              </CardSection>
            </Card>
          </>
        )}
      </div>
    </main>
  );
}
